import type { ComponentType } from 'preact'
import { useMemo, useRef, useState } from 'preact/hooks'
import type { GamePhase } from '#contract'
import { BootScreen } from '../screens/boot-screen.tsx'
import { CreditsScreen } from '../screens/credits/credits-screen.tsx'
import { ErrorScreen } from '../screens/error-screen.tsx'
import { LoadingScreen } from '../screens/loading-screen.tsx'
import { MainMenuScreen } from '../screens/main-menu-screen.tsx'
import { PauseScreen } from '../screens/pause-screen.tsx'
import { LoadGameScreen, NewGameScreen } from '../screens/save-slots/save-slots-screen.tsx'
import { SettingsScreen } from '../screens/settings/settings-screen.tsx'
import { useClient } from './client-context.ts'
import type { ScreenStack, StackScreen } from './screen-stack.ts'
import { ScreenStackContext } from './screen-stack.ts'
import { useUiIntents } from './use-ui-intents.ts'
import { useView } from './use-view.ts'

/** The full-screen UI for each phase. Phases without an entry (`playing`) show no screen. */
const SCREENS: Partial<Record<GamePhase, ComponentType>> = {
  boot: BootScreen,
  loading: LoadingScreen,
  menu: MainMenuScreen,
  paused: PauseScreen,
  error: ErrorScreen,
}

const STACK_SCREENS: Record<StackScreen, ComponentType> = {
  credits: CreditsScreen,
  settings: SettingsScreen,
  newGame: NewGameScreen,
  loadGame: LoadGameScreen,
}

/** Shows the top of the screen stack, or else the current phase's screen. */
export function ScreenRouter() {
  const client = useClient()
  const phase = useView((view) => view.phase)
  const [screens, setScreens] = useState<readonly StackScreen[]>([])
  const [stackPhase, setStackPhase] = useState(phase)
  const backOverrideRef = useRef<(() => void) | null>(null)
  // Any phase change (a level load, say) closes what was opened on top.
  if (stackPhase !== phase) {
    setStackPhase(phase)
    setScreens([])
  }

  const stack = useMemo<ScreenStack>(
    () => ({
      push: (screen) => {
        setScreens((current) => [...current, screen])
      },
      back: () => {
        setScreens((current) => current.slice(0, -1))
      },
      overrideBack: (onBack) => {
        backOverrideRef.current = onBack
      },
    }),
    [],
  )

  const top = stackPhase === phase ? screens.at(-1) : undefined
  // Back closes the top screen, or resumes from the pause screen itself. While playing it
  // belongs to the phone or computer that's up.
  useUiIntents(() => {
    if (phase === 'playing') return
    client?.playUiSound('back')
    if (backOverrideRef.current) backOverrideRef.current()
    else if (top !== undefined) stack.back()
    else if (phase === 'paused') client?.resume()
  })

  const Screen = top === undefined ? SCREENS[phase] : STACK_SCREENS[top]
  return (
    <ScreenStackContext.Provider value={stack}>
      {Screen ? <Screen /> : null}
    </ScreenStackContext.Provider>
  )
}
